/**
 * Pagination Component
 * 
 * Unified pagination component using MUI Pagination.
 * Supports multiple variants and customization options.
 * 
 * @example
 * // Simple pagination
 * <Pagination
 *   page={currentPage}
 *   totalPages={10}
 *   onPageChange={setPage}
 * />
 * 
 * @example
 * // With item count info
 * <Pagination
 *   page={currentPage}
 *   totalPages={10}
 *   onPageChange={setPage}
 *   totalItems={100}
 *   pageSize={10}
 *   showItemCount
 * />
 */

import React from 'react';
import {
  Box,
  Pagination as MuiPagination,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';

// =============================================================================
// TYPES
// =============================================================================

export interface PaginationProps {
  /** Current page number (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Total number of items (for showing "X of Y items") */
  totalItems?: number;
  /** Current page size */
  pageSize?: number;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Callback when page size changes */
  onPageSizeChange?: (size: number) => void;
  /** Show "X of Y items" info */
  showItemCount?: boolean;
  /** Show page size selector */
  showPageSizeSelector?: boolean;
  /** Variant style */
  variant?: 'default' | 'simple' | 'compact';
  /** MUI color */
  color?: 'primary' | 'secondary' | 'standard';
  /** Shape of pagination items */
  shape?: 'circular' | 'rounded';
  /** Size of pagination */
  size?: 'small' | 'medium' | 'large';
  /** Container class name */
  className?: string;
  /** Container styles */
  sx?: object;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  totalItems,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  showItemCount = false,
  showPageSizeSelector = false,
  variant = 'default',
  color = 'primary',
  shape = 'rounded',
  size = 'medium',
  className = '',
  sx = {},
}) => {
  if (totalPages <= 1 && !showItemCount) return null;

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange(value);
  };

  // Calculate item range
  const startItem = totalItems ? (page - 1) * pageSize + 1 : 0;
  const endItem = totalItems ? Math.min(page * pageSize, totalItems) : 0;

  // Simple variant - just prev/next with text
  if (variant === 'simple') {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          pt: 2,
          ...sx,
        }}
      >
        <MuiPagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          color={color}
          shape={shape}
          size={size}
          hidePrevButton={false}
          hideNextButton={false}
          showFirstButton={false}
          showLastButton={false}
          siblingCount={0}
          boundaryCount={0}
        />
        <Typography variant="body2" color="text.secondary">
          Page {page} of {totalPages}
        </Typography>
      </Box>
    );
  }

  // Compact variant - minimal
  if (variant === 'compact') {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 1,
          ...sx,
        }}
      >
        <MuiPagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          color={color}
          shape={shape}
          size="small"
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    );
  }

  // Default variant - full featured
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        pt: 2,
        ...sx,
      }}
    >
      {/* Item count info */}
      {showItemCount && totalItems !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Showing {startItem}-{endItem} of {totalItems.toLocaleString()} items
        </Typography>
      )}

      {/* Pagination controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mx: 'auto' }}>
        <MuiPagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          color={color}
          shape={shape}
          size={size}
          showFirstButton={totalPages > 5}
          showLastButton={totalPages > 5}
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>

      {/* Page size selector */}
      {showPageSizeSelector && onPageSizeChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small" variant="outlined">
            <Select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              sx={{ minWidth: 70 }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default Pagination;
